export const GAME_WIDTH = 960;

// Dynamic game height: fill the device viewport while keeping 960px logical width
let _gameHeight = 1440; // default fallback

export function getGameHeight(): number {
  return _gameHeight;
}

export function initGameHeight(): void {
  // Compute logical height that maps to the full viewport at 960px width.
  // The container has max-width: 960px, so on wide viewports the effective
  // width is capped at 960 — use that for the aspect-ratio calculation so
  // the canvas fills the full viewport height after Phaser's FIT scaling.
  const vw = Math.min(window.innerWidth || 960, GAME_WIDTH);
  const vh = window.innerHeight || 1440;
  _gameHeight = Math.round((vh / vw) * GAME_WIDTH);
  // Clamp to reasonable range
  if (_gameHeight < 1440) _gameHeight = 1440;
  if (_gameHeight > 2400) _gameHeight = 2400;
}

// Board — 42×42 grid at 16px cells = 672px (same pixel area as old 24×14)
// These are kept as defaults / fallbacks; per-level geometry comes from BoardGeometry.
export const BOARD_COLS = 42;
export const BOARD_ROWS = 42;
export const CELL_SIZE = 16;
export const BOARD_OFFSET_X = 144;
export const BOARD_OFFSET_Y = 180;

// Re-export BoardGeometry for convenience
export { type BoardGeometry, computeBoardGeometry } from './BoardGeometry';

// Conveyor
export const CONVEYOR_SPEED = 440; // pixels per second
export const CONVEYOR_MAX_ATTACKERS = 5;
export const CONVEYOR_PADDING = 50; // distance from board edge to conveyor path

// Queue
export const MAX_STACK_SLOTS = 5;
export const DEFAULT_NUM_QUEUES = 3;
export const MAX_NUM_QUEUES = 6;
export const QUEUE_LANE_GAP = 20;
/** Compute lane width dynamically based on queue count. Fits within GAME_WIDTH with margins. */
export function getQueueLaneWidth(numQueues: number = DEFAULT_NUM_QUEUES): number {
  const totalGap = (numQueues - 1) * QUEUE_LANE_GAP;
  const margin = 32; // 16px each side
  return Math.floor((GAME_WIDTH - totalGap - margin) / numQueues);
}
/** Total width occupied by all queue lanes including gaps. */
export function getTotalLanesWidth(numQueues: number = DEFAULT_NUM_QUEUES): number {
  const laneWidth = getQueueLaneWidth(numQueues);
  return numQueues * laneWidth + (numQueues - 1) * QUEUE_LANE_GAP;
}
/** X position where the first lane starts (centered in GAME_WIDTH). */
export function getLanesStartX(numQueues: number = DEFAULT_NUM_QUEUES): number {
  return (GAME_WIDTH - getTotalLanesWidth(numQueues)) / 2;
}
export const QUEUE_ROW_SPACING = 88;
export const MAX_VISIBLE_QUEUE_ROWS = 3;

// Shooting
export const PROJECTILE_SPEED = 1400; // pixels per second

// Difficulty defaults
export const DEFAULT_NUM_COLORS = 4;
export const DEFAULT_STACK_DEPTH = 1;

// Queue peek
export const QUEUE_PEEK_USES = 999; // temp: unlimited peeks (was 3)
export const QUEUE_PEEK_DURATION = 3000; // ms

// UI
export const FONT_FAMILY = 'Outfit, sans-serif';
export const HUD_HEIGHT = 100;
// Waiting slots positioned just below the conveyor belt
export function getStackY(geo?: import('./BoardGeometry').BoardGeometry): number {
  const offsetY = geo ? geo.offsetY : BOARD_OFFSET_Y;
  const boardHeight = geo ? geo.boardHeight : BOARD_ROWS * CELL_SIZE;
  return offsetY + boardHeight + CONVEYOR_PADDING + 136;
}
// Queue starts below the waiting slots + peek button, stretches to the bottom
export function getQueueY(geo?: import('./BoardGeometry').BoardGeometry): number {
  return getStackY(geo) + 160;
}
export const SLOT_SIZE = 96;
export const ATTACKER_SIZE = 68;
export const CONVEYOR_ATTACKER_SCALE = 1.3; // attackers appear ~30% larger on the conveyor

// Lookahead depth for bot dispatch evaluation
export const DEFAULT_LOOKAHEAD_DEPTH = 2;
export const RUNTIME_LOOKAHEAD_DEPTH = 3;
export const AUDIT_LOOKAHEAD_DEPTH = 3;
export const MAX_LOOKAHEAD_DEPTH = 5;
export const RUNTIME_ROLLOUT_DEPTH = 5;
export const SOLVE_ROLLOUT_BUDGET = 9999;
